import {
  computed,
  ref,
  useContext,
  useRoute,
  useRouter,
  watch
} from "@nuxtjs/composition-api";
import useFetchDocuments from "./useFetchDocuments";
import useFetchProducts from "./useFetchProducts";
import usePagePreloader from "./usePagePreloader";
function prepareDocsResults(products, docs) {
  return products.map((product) => {
    const { id, sku, name, slug, variations } = product;
    return {
      id,
      name,
      slug,
      image: variations.find((vr) => vr.sku === sku)?.images[0],
      documents: docs[product.sku]
    };
  }).filter(Boolean);
}
function getPopularQueries(translation) {
  return String(translation || "").split(";").map((item) => item.trim()).filter(Boolean);
}
function useSearch() {
  const { i18n } = useContext();
  const router = useRouter();
  const route = useRoute();
  const { fetchProductsByName } = useFetchProducts();
  const { documents, DOCUMENT_TYPES, fetchDocumentsGroupedByProducts } = useFetchDocuments();
  const preloader = usePagePreloader("search");
  const pageMeta = ref();
  const searchResults = ref();
  const searchValue = ref("");
  const popularSearches = ref(
    getPopularQueries(i18n.t("popular_queries_items"))
  );
  const searchQuery = computed(() => route.value.query.search);
  const currentPage = computed(() => route.value.query.page);
  const totalCount = computed(() => pageMeta.value?.total ?? 0);
  const pagesCount = computed(() => pageMeta.value?.last_page ?? 1);
  const searchType = computed(
    () => route.value.path.startsWith("/search") ? route.value.path.split("/").pop() : ""
  );
  const updateRouter = (type) => {
    router.push({
      path: `/search/${type}`,
      query: {
        search: searchValue.value,
        page: currentPage.value
      }
    });
  };
  const scrollUp = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  const clearResults = () => {
    searchResults.value = null;
    pageMeta.value = null;
  };
  const getProducts = async (queryString) => {
    return await fetchProductsByName(queryString, {
      limit: 12,
      page: currentPage.value
    });
  };
  const searchProducts = async () => {
    scrollUp();
    clearResults();
    updateRouter("products");
    if (!searchValue.value)
      return;
    try {
      preloader.start();
      const result = await getProducts(searchValue.value);
      searchResults.value = result?.data;
      pageMeta.value = result?.meta;
    } finally {
      preloader.stop();
    }
  };
  const searchDocuments = async () => {
    scrollUp();
    clearResults();
    updateRouter("documents");
    if (!searchValue.value)
      return;
    try {
      preloader.start();
      const products = await getProducts(searchValue.value);
      if (products?.meta.total) {
        await fetchDocumentsGroupedByProducts(
          { product_skus: products.data.map((product) => product.sku) },
          { exclude: [DOCUMENT_TYPES.VIDEOS] }
        );
        if (documents.value) {
          searchResults.value = prepareDocsResults(
            products.data,
            documents.value
          );
          pageMeta.value = products.meta;
        }
      }
    } finally {
      preloader.stop();
    }
  };
  watch(searchQuery, (value) => {
    if (typeof value === "string") {
      searchValue.value = value;
    } else if (Array.isArray(value) && value[0] !== null) {
      searchValue.value = value[0];
    } else {
      searchValue.value = "";
    }
  });
  return {
    preloader,
    pageMeta,
    searchResults,
    searchQuery,
    searchValue,
    currentPage,
    totalCount,
    pagesCount,
    popularSearches,
    searchType,
    searchProducts,
    searchDocuments,
    updateRouter
  };
}
var useSearch_default = useSearch;
export {
  useSearch_default as default
};
